import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import betslip from '@gin-betting/betslip';
import core, { App, GQL, View } from '@gin-betting/core';
import { first, includes } from 'lodash';
import { createSelector } from 'reselect';

import { useMemoSelector } from 'hooks';
import ui from 'modules/ui';
import { ODDS_CHANGE_DECLINE_REASONS } from '../../constants';

const {
    entities: {
        selectors: { selectBets },
    },
} = core;

const {
    selectors: { selectChosenBetslipBet, selectBetslipOddsByChosenBetslipBet },
    actions: { changeOddUpdateStatus },
    utils: { getStatus },
} = betslip;

const {
    selectors: { selectIsOddsChangeModalShown },
    actions: { setOddsChangeModalShown },
} = ui;

interface Output {
    handleSkip: VoidFn;
    handleCancel: VoidFn;
    handleSubmit: VoidFn;
    isModalOpen: boolean;
}

const useAcceptOddsChangeModal = (): Output => {
    const dispatch = useDispatch();

    const [isOpen, setOpen] = useState<boolean>(false);

    const { hasOddsChangeError, isErrorBetslip } = useMemoSelector(selectProps);

    const handleSkip = () => setOpen(false);
    const handleSubmit = () => {
        dispatch(
            changeOddUpdateStatus({
                oddUpdateStatus: App.OddUpdateStatus.acceptAll,
            })
        );
        dispatch(setOddsChangeModalShown());
        setOpen(false);
    };

    const handleCancel = () => {
        dispatch(setOddsChangeModalShown());
        setOpen(false);
    };

    useEffect(() => {
        setOpen(!!isErrorBetslip && hasOddsChangeError);
    }, [hasOddsChangeError, isErrorBetslip]);

    return {
        handleSkip,
        handleCancel,
        handleSubmit,
        isModalOpen: isOpen,
    };
};

const selectProps = createSelector(
    selectChosenBetslipBet,
    selectBetslipOddsByChosenBetslipBet,
    selectIsOddsChangeModalShown,
    selectBets,
    (betslipBet, betslipOdds, isOddsChangeModalShown, bets) => {
        let hasOddsChangeError = false;

        if (
            isOddsChangeModalShown ||
            !betslipBet ||
            betslipOdds.count() === 0
        ) {
            return {
                hasOddsChangeError,
            };
        }

        hasOddsChangeError = betslipOdds.some(({ betId }) => {
            if (!betId) return false;

            const bet = bets.get(betId);

            if (!bet) return false;

            return (
                includes(ODDS_CHANGE_DECLINE_REASONS, bet.declineReason) ||
                hasOddChangeRestriction(bet)
            );
        });

        const isErrorBetslip =
            getStatus({
                betslipOddIds: betslipBet.betslipOddIds,
                betslipOdds,
            }) === App.BetslipStatus.ERROR;

        return {
            isErrorBetslip,
            hasOddsChangeError,
            betslipBetId: betslipBet.id,
        };
    }
);

function hasOddChangeRestriction(bet: View.Bet): boolean {
    if (bet.declineContext.length === 0) return false;

    const declineContextItem = first(
        bet.declineContext
    ) as GQL.BetDeclineContextRestrictions;

    return Boolean(
        declineContextItem.restrictions.find(
            r => r.type === GQL.RestrictionType.selection_value
        )
    );
}

export default useAcceptOddsChangeModal;
